import 'rc-slider/assets/index.css';
import styles from 'css/src/components/common/ui/Slider.module.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import type {
    TypeInputChangeEvent,
    TypeInputFocusEvent,
    TypeInputKeyboardEvent,
} from './TextInput';
import TextInput from './TextInput';

import { roundToPrecision } from 'js/src/libs/math';
import RSSlider from 'rc-slider';

type Props = {
    defaultValue: number;
    icon?: string;
    iconAfter?: string;
    input?: boolean; // add additional input field next to slider to show and modify slider's value
    inputUnit?: string;
    min?: number;
    max?: number;
    step?: number;
    onChange?: (value: number) => void;
    onBeforeChange?: (value: number) => void;
    onAfterChange?: (value: number) => void;
    className?: string;
    iconAfterClassName?: string;
    iconClassName?: string;
    disabled?: boolean;
    startPoint?: number;
};

function precisionFromStep(step: number) {
    return step.toString().split('.')[1]?.length || 0;
}

function Slider({
    defaultValue = 0,
    min = 0,
    max = 100,
    step = 1,
    onChange,
    onBeforeChange,
    onAfterChange,
    icon = '',
    iconAfter = '',
    input = false,
    inputUnit = '',
    className = '',
    iconAfterClassName = '',
    iconClassName = '',
    disabled = false,
    startPoint,
}: Props) {
    useEffect(() => {
        setSliderValue(defaultValue);
        setInputValue(defaultValue);
    }, [defaultValue]);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [sliderValue, setSliderValue] = useState(defaultValue);
    const [inputValue, setInputValue] = useState(defaultValue);
    const [showInputUnit, setShowInputUnit] = useState(true);

    const handleInputChange = (e: TypeInputChangeEvent) => {
        const value = e.target.value || '';
        setInputValue(parseFloat(value));
    };

    const handleInputBlur = (_e: TypeInputFocusEvent) => {
        updateFromInputValue();
        setShowInputUnit(true);
    };

    const handleInputFocus = (_e: TypeInputFocusEvent) => {
        setShowInputUnit(false);
    };

    const handleBeforeChange = (value: number | number[]) => {
        // bug with rc-slider types requires the number option, see
        // https://github.com/react-component/slider/issues/825
        if (typeof value !== 'number') {
            throw new Error('Expected values to be a number');
        }
        onBeforeChange?.(value);
    };

    const handleAfterChange = (value: number | number[]) => {
        // bug with rc-slider types requires the number option, see
        // https://github.com/react-component/slider/issues/825
        if (typeof value !== 'number') {
            throw new Error('Expected values to be a number');
        }
        onAfterChange?.(value);
    };

    const handleKeyDown = (e: TypeInputKeyboardEvent) => {
        if (e.key === 'Enter') {
            updateFromInputValue();
        }
    };

    const updateFromInputValue = () => {
        let value = inputValue;
        if (isNaN(value)) {
            value = defaultValue;
        }
        // round value to a precision based on the step
        const precision = precisionFromStep(step);
        value = roundToPrecision(value, precision);
        if (value > max) {
            value = max;
        }
        if (value < min) {
            value = min;
        }
        setSliderValue(value);
        setInputValue(value);
        if (typeof onChange === 'function') {
            onChange(value);
        }
        if (typeof onAfterChange === 'function') {
            onAfterChange(value);
        }
    };

    const handleSliderChange = (value: number | number[]) => {
        // bug with rc-slider types requires the number option, see
        // https://github.com/react-component/slider/issues/825
        if (typeof value !== 'number') {
            throw new Error('Expected values to be a number');
        }
        setSliderValue(value);
        setInputValue(value);
        if (typeof onChange === 'function') {
            onChange(value);
        }

        // Ensure the slider handle stays focused after track clicks
        const handle = containerRef.current?.querySelector('.rc-slider-handle') as HTMLElement;
        if (handle && !handle.matches(':focus')) {
            handle.focus();
        }
    };
    let inputText = '';
    if (!isNaN(inputValue)) {
        const precision = precisionFromStep(step);
        const numberWithoutInsignificantZeros = (+inputValue.toFixed(precision)).toString();
        inputText = numberWithoutInsignificantZeros + (showInputUnit ? inputUnit : '');
    }

    const classNameWithDisabled = classNames(styles['slider-container'], className, {
        [styles['disabled']]: disabled,
    });

    // Handle focus/blur events to prevent unwanted blur propagation
    const handleContainerBlur = useCallback((e: React.FocusEvent) => {
        const isBlurFromSlider = e.target.closest('.rc-slider');
        const isFocusToSlider = e.relatedTarget?.closest?.('.rc-slider');
        const isBlurFromSliderInput = e.target.closest('.prevent-restore-cursor');
        // If blur is from slider or slider input and focus is going to slider or nowhere, prevent propagation
        if ((isBlurFromSlider || isBlurFromSliderInput) && (isFocusToSlider || !e.relatedTarget)) {
            e.stopPropagation();
        }
    }, []);

    return (
        <div className={classNameWithDisabled} onBlur={handleContainerBlur}>
            {icon && (
                <i translate="no" className={`material-symbols-outlined ${iconClassName}`}>
                    {icon}
                </i>
            )}
            <div className="w-100" ref={containerRef}>
                <RSSlider
                    min={min}
                    max={max}
                    step={step}
                    defaultValue={defaultValue}
                    value={sliderValue}
                    onChange={handleSliderChange}
                    onBeforeChange={handleBeforeChange}
                    onChangeComplete={handleAfterChange}
                    disabled={disabled}
                    startPoint={startPoint}
                />
            </div>
            {iconAfter && (
                <i translate="no" className={`material-symbols-outlined ${iconAfterClassName}`}>
                    {iconAfter}
                </i>
            )}
            {input && (
                <TextInput
                    type="text"
                    className="form-control prevent-restore-cursor"
                    text={inputText}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    onFocus={handleInputFocus}
                    disabled={disabled}
                    selectOnFocus={true}
                />
            )}
        </div>
    );
}
export default Slider;

Slider.displayName = 'Slider';
